<template>
  <article
    :class="[extraClasses, shouldDisplayAsPinned ? 'flex-col md:flex-row' : 'flex-col']"
    class="story hn-card group relative flex overflow-hidden rounded-card border border-solid border-line bg-surface"
    data-testid="StoryCard"
  >
    <router-link
      v-if="showCover"
      :to="linkPath"
      :class="[
        isLarger ? 'aspect-[16/8.4]' : 'aspect-[16/10]',
        shouldDisplayAsPinned ? 'md:order-2 md:aspect-auto md:w-2/3' : ''
      ]"
      class="story__thumb relative overflow-hidden bg-[#dfe6e3]"
    >
      <div
        v-observe-visibility="observeCallback"
        :style="coverStyle"
        class="story__thumb-inner absolute inset-0 flex items-center justify-center bg-cover bg-center bg-no-repeat p-4 font-display text-lg font-semibold text-white"
      >
        <!-- NEWSROOM DATA (no-cover fallback) — dark scrim keeps the name legible
             on any newsroom `main_color`, light or dark -->
        <span
          v-if="!newsroomLogo && !hasCover"
          class="rounded-md bg-black/45 px-3 py-1.5 text-center"
        >{{ newsroom.name }}</span>
      </div>
    </router-link>

    <div
      :class="[
        isLarger ? 'gap-3 p-8' : 'gap-2.5 px-[17px] pt-[15px] pb-[18px]',
        shouldDisplayAsPinned ? 'md:order-1 md:w-1/3' : ''
      ]"
      class="story__body flex flex-1 flex-col"
    >
      <story-card-newsroom-head
        :newsroom="newsroom"
        :story="story"
        :newsroom-path-name="newsroomPathName"
        :hide-date="hideDate || (isEmbedded && storySettings.dateStamp)"
      >
        <!-- Larger/featured card: story type + country sit inline in the head row -->
        <template v-if="isLarger" #meta>
          <span
            v-if="showStoryType"
            :title="typeLabel"
            class="story__type shrink-0 rounded-full bg-accent-soft px-2.5 py-1 font-display text-xs font-bold text-accent"
          >
            {{ typeLabel }}
          </span>

          <div
            v-if="showCountry"
            class="flex shrink-0 items-center gap-1.5 text-sm text-muted"
          >
            <country-flag v-if="newsroomCountry.iso_alpha2" :country="newsroomCountry.iso_alpha2" />
            <span class="font-semibold">{{ newsroomCountry.name }}</span>
          </div>
        </template>

        <a
          v-if="isEditable"
          :href="editUrl"
          target="hn_story_edit"
          class="story__action flex h-8 w-8 items-center justify-center rounded-full bg-bg text-muted transition-colors hover:bg-accent hover:text-white"
          data-testid="StoryCard__edit"
        >
          <v-icon type="pencil" />
        </a>
      </story-card-newsroom-head>

      <router-link
        :to="linkPath"
        class="flex flex-1 flex-col gap-2.5"
        data-testid="StoryCard_StoryLink"
      >
        <h3
          :title="story.headline"
          :class="isLarger
            ? 'text-[28px] leading-[1.1] tracking-[-0.03em]'
            : 'text-lg leading-[1.22] tracking-[-0.015em]'"
          class="story__title font-display font-bold text-ink"
        >
          {{ truncate(story.headline, 60) }}
        </h3>

        <p
          v-if="!hideContent"
          class="story__text hidden font-sans text-base leading-relaxed text-muted md:block"
        >
          {{ stripHtml(truncate(secondaryContentToShow, copyTextChars)) }}
        </p>

        <!-- Normal card: country (left) + story type (right), pinned to the card bottom
             via `mt-auto` so the row stays aligned regardless of title/excerpt length. -->
        <div
          v-if="!isLarger && (showStoryType || showCountry)"
          class="mt-auto flex items-center gap-2"
        >
          <div
            v-if="showCountry"
            class="flex shrink-0 items-center gap-1.5 text-sm text-muted"
          >
            <country-flag v-if="newsroomCountry.iso_alpha2" :country="newsroomCountry.iso_alpha2" />
            <span class="font-semibold">{{ newsroomCountry.name }}</span>
          </div>

          <span
            v-if="showStoryType"
            :title="typeLabel"
            class="story__type ml-auto shrink-0 rounded-full bg-accent-soft px-2.5 py-1 font-display text-xs font-bold text-accent"
          >
            {{ typeLabel }}
          </span>
        </div>
      </router-link>
    </div>
  </article>
</template>

<script>
import { mapGetters } from 'vuex'

import DatetimeMixin from '@/shared/js/mixins/DatetimeMixin'
import StripHtmlMixin from '@/shared/js/mixins/StripHtmlMixin'
import TruncateMixin from '@/shared/js/mixins/TruncateMixin'

import StoryCardNewsroomHead from './StoryCardNewsroomHead'

export default {
  components: {
    StoryCardNewsroomHead
  },

  mixins: [DatetimeMixin, StripHtmlMixin, TruncateMixin],

  props: {
    story: {
      type: Object,
      required: true
    },
    isLarger: {
      type: Boolean,
      default: false
    },
    newsroomProp: {
      type: Object,
      default: () => ({})
    },
    showLessInfo: {
      type: Boolean,
      default: false
    },
    showPinnedPosts: {
      type: Boolean,
      default: false
    },
    storyPathName: {
      type: String,
      default: 'story'
    },
    newsroomPathName: {
      type: String,
      default: 'newsroom.stories'
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    hideContent: {
      type: Boolean,
      default: false
    },
    copyTextChars: {
      type: Number,
      default: 160
    }
  },

  data () {
    return {
      inViewport: false,
      observeCallback: {
        callback: this.onViewportEnter,
        once: true
      }
    }
  },

  computed: {
    ...mapGetters([
      'isEmbedded',
      'storySettings'
    ]),

    coverStyle () {
      let style = {}
      if (this.inViewport) {
        style = {
          backgroundImage: `url('${this.storyCover}')`
        }
      }
      if (!this.hasCover) {
        style = {
          ...style,
          borderColor: this.newsroom.main_color,
          backgroundColor: this.newsroom.main_color
        }
      }
      return style
    },

    extraClasses () {
      return {
        'story--no-cover': !this.hasCover,
        'story--in-viewport': this.inViewport
      }
    },

    showCover () {
      return !(this.isEmbedded && this.storySettings.storyImage)
    },

    shouldDisplayAsPinned () {
      return this.showPinnedPosts && this.isPinned
    },

    isPinned () {
      return this.story.is_pinned
    },

    newsroom () {
      const newsroom = this.$safeGet(this.story.newsroom, 'data', {})
      return this.$isEmpty(newsroom) ? this.newsroomProp : newsroom
    },

    hasCover () {
      return !!this.story.cover.cropped
    },

    storyCover () {
      if (!this.hasCover) {
        return this.newsroomLogo
      }

      return ((this.isLarger || this.shouldDisplayAsPinned) && this.hasScaledSizes)
        ? this.$safeGet(this.story, 'cover.scaled.medium.path', this.story.cover.cropped)
        : this.story.cover.cropped
    },

    hasScaledSizes () {
      return !!this.story.cover.scaled
    },

    newsroomLogo () {
      return this.$safeGet(this.newsroom, 'logo.cropped')
    },

    newsroomCountry () {
      return this.$safeGet(this.newsroom, 'country.data', null)
    },

    typeLabel () {
      return this.$t('components.story_types.' + this.story.type)
    },

    showStoryType () {
      return !!this.story.type && !(this.isEmbedded && this.storySettings.storyTypeTag)
    },

    // Country is hidden on newsroom/related surfaces (`fromNewsroomPage`) where the
    // story's own country is redundant — preserves the old footer visibility rule.
    showCountry () {
      return !!this.newsroomCountry && !this.fromNewsroomPage
    },

    fromNewsroomPage () {
      return this.showLessInfo || !this.story.newsroom
    },

    pathParams () {
      return {
        storyId: this.story.id,
        storySlug: this.story.slug,
        newsroomId: this.newsroom.id,
        newsroomSlug: this.story.newsroom_slug
      }
    },

    linkPath () {
      return {
        name: this.storyPathName,
        params: this.pathParams
      }
    },

    secondaryContentToShow () {
      return this.story.subheadline || this.story.excerpt
    },

    editUrl () {
      return this.$getUrl(`stories/story/${this.story.id}/edit?brand=${this.newsroom.brand.id}`, 'hypefactors', false)
    }
  },

  methods: {
    onViewportEnter (isVisible, instance) {
      this.inViewport = isVisible
    }
  }
}
</script>

<style lang="scss">
.story {
  // Hover lift + elevation — mirrors the mockups' `.hn-card` treatment.
  box-shadow: 0 1px 2px rgba(22, 34, 30, .05), 0 8px 20px -15px rgba(22, 34, 30, .18);
  transition: box-shadow .18s ease, transform .18s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 2px 4px rgba(22, 34, 30, .06), 0 18px 34px -18px rgba(22, 34, 30, .3);
  }

  // CountryFlag ships a Bulma `m-r-s` (margin-right !important); the card already spaces
  // the flag with `gap-1.5`, so zero it out locally — shared component left untouched.
  .flag {
    margin-right: 0 !important;
  }

  // Lazy cover: blur-up until the thumb scrolls into the viewport.
  &__thumb-inner {
    filter: blur(5px);
    transition: filter .3s ease-in;
  }

  &--in-viewport &__thumb-inner {
    filter: none;
  }

  // No-cover fallback keeps the newsroom main-color band (border + bg set inline
  // via `coverStyle`); contain the logo so it doesn't crop oddly.
  &--no-cover &__thumb-inner {
    border-style: solid;
    border-width: 24px 12px;
    background-size: contain;
  }

  // Dark card variant — applied externally by the feed (`storyExtraClass`).
  &.is-black {
    background: #15191a;

    &,
    .story__title,
    .story__body a {
      color: #ffffff;
    }

    .story__action {
      background: rgba(255, 255, 255, .12);
      color: #ffffff;
    }
  }
}
</style>
